import type { RestrictedWord } from "@/shared/cache/slices/restrictedWords";

export type RestrictedWordMatch = {
  text: string;
  start: number;
  end: number;
  severity: "warning" | "block";
  displayName: string;
  suggestion: string | null;
};

export function detectRestrictedWords(
  text: string,
  words: RestrictedWord[],
): RestrictedWordMatch[] {
  if (!text || !words.length) return [];

  const matches: RestrictedWordMatch[] = [];

  for (const w of words) {
    // Reset lastIndex for global regexes
    const pattern = new RegExp(w.pattern.source, w.pattern.flags);
    let m: RegExpExecArray | null;

    while ((m = pattern.exec(text)) !== null) {
      matches.push({
        text: m[0],
        start: m.index,
        end: m.index + m[0].length,
        severity: w.severity,
        displayName: w.displayName,
        suggestion: w.suggestion,
      });
      // Prevent infinite loop for zero-length matches
      if (m[0].length === 0) pattern.lastIndex++;
    }
  }

  // Sort by position, block-first on ties
  matches.sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    return a.severity === "block" && b.severity !== "block" ? -1 : 1;
  });

  return matches;
}

export function mergeOverlappingMatches(
  matches: RestrictedWordMatch[],
): RestrictedWordMatch[] {
  if (matches.length <= 1) return matches;

  const result: RestrictedWordMatch[] = [matches[0]];

  for (let i = 1; i < matches.length; i++) {
    const current = matches[i];
    const prev = result[result.length - 1];

    if (current.start < prev.end) {
      // Overlapping — block wins
      if (current.severity === "block" && prev.severity !== "block") {
        result[result.length - 1] = {
          ...current,
          start: Math.min(prev.start, current.start),
          end: Math.max(prev.end, current.end),
          text: "",  // Will be recalculated by consumer from source text
        };
      } else {
        // Extend previous match — text is stale; consumers must re-slice from source
        result[result.length - 1] = {
          ...prev,
          end: Math.max(prev.end, current.end),
          text: "",
        };
      }
    } else {
      result.push(current);
    }
  }

  return result;
}
